import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import axiosInstance from "../axiosInstance";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

export interface ApiCallConfig {
  method: "get" | "post" | "put" | "delete" | "patch";
  url: string;
  data?: any;
  params?: Record<string, any>;
}

async function makeApiCall<T>(config: ApiCallConfig): Promise<T> {
  const accessToken = Cookies.get("access-token");
  try {
    const axiosConfig: AxiosRequestConfig = {
      method: config.method,
      url: config.url,
      data: config.data,
      params: config.params,
      headers: accessToken ? { Authorization: `${accessToken}` } : {},
    };

    const response: AxiosResponse<T> = await axiosInstance(axiosConfig);
    return response.data;
  } catch (error: any) {
    console.log("Error intercepted in makeApiCall:", error);
    // Check if we're on a public quotation page - don't redirect for those
    const isPublicQuotationPage =
      window.location.pathname.includes("/public-links/");

    if (error.message === "Token Expired" && !isPublicQuotationPage) {
      Swal.fire({
        title: "Session Expired",
        text: "Re-login to continue",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
      }).then(async (result) => {
        if (result.isConfirmed) {
          window.location.href = "/";
        }
      });
      await new Promise((resolve) => setTimeout(resolve, 10000));
      window.location.href = "/";
    }
    return error;
  }
}

// Separate API call function for public quotation routes (uses public-token cookie)
export async function makePublicApiCall<T>(config: ApiCallConfig): Promise<T> {
  const publicToken = Cookies.get("public-token");
  try {
    const headers: any = publicToken ? { Authorization: `${publicToken}` } : {};

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (!(config.data instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const axiosConfig: AxiosRequestConfig = {
      method: config.method,
      url: config.url,
      data: config.data,
      params: config.params,
      headers,
    };

    const response: AxiosResponse<T> = await axiosInstance(axiosConfig);
    return response.data;
  } catch (error: any) {
    console.log("Error intercepted in makePublicApiCall:", error);
    // Check for token expiration or 401 errors
    if (
      error.message === "Token Expired" ||
      (error?.response?.status === 401 &&
        error?.response?.data?.message?.toLowerCase().includes("token"))
    ) {
      // Clear public token
      Cookies.remove("public-token");
      Cookies.remove("public-refresh-token");
      // Trigger session expired handler via custom event
      window.dispatchEvent(new CustomEvent("public-session-expired"));
      Swal.fire({
        title: "Session Expired",
        text: "Please verify again",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
      });
    }
    return error;
  }
}

// API call function without any authorization token
export async function makeUnauthenticatedApiCall<T>(
  config: ApiCallConfig
): Promise<T> {
  try {
    // Create a new axios instance without interceptors to avoid token injection
    const unauthenticatedAxios = axios.create({
      baseURL: process.env.REACT_APP_API_LOGIN_URL,
      withCredentials: false, // Don't send cookies
    });

    const headers: any = {};

    // Set Content-Type only for non-FormData requests
    if (!(config.data instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }
    // Explicitly ensure no Authorization header
    delete headers.Authorization;

    const axiosConfig: AxiosRequestConfig = {
      method: config.method,
      url: config.url,
      data: config.data,
      params: config.params,
      headers,
    };

    const response: AxiosResponse<T> = await unauthenticatedAxios(axiosConfig);
    return response.data;
  } catch (error: any) {
    console.log("Error intercepted in makeUnauthenticatedApiCall:", error);
    // Return error response data if available, otherwise return the error itself
    if (error?.response?.data) {
      return error.response.data as T;
    }
    return error;
  }
}

export default makeApiCall;
